import {
  GridColDef,
  GridColumnGroupingModel,
  GridRowSelectionModel,
  GridRowsProp,
} from '@mui/x-data-grid-pro';
import {Checkbox} from '@wandb/weave/components/Checkbox';
import {Pill} from '@wandb/weave/components/Tag';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import {UserLink} from '@wandb/weave/components/UserLink';
import _ from 'lodash';
import React, {useEffect, useMemo, useState} from 'react';

import {TEAL_600} from '../../../../../../common/css/color.styles';
import {ErrorPanel} from '../../../../../ErrorPanel';
import {Loading} from '../../../../../Loading';
import {LoadingDots} from '../../../../../LoadingDots';
import {Timestamp} from '../../../../../Timestamp';
import {StyledDataGrid} from '../../StyledDataGrid';
import {basicField} from '../common/DataTable';
import {Empty, EmptyProps} from '../common/Empty';
import {
  EMPTY_PROPS_EVALUATIONS,
  EMPTY_PROPS_LEADERBOARDS,
  EMPTY_PROPS_MODEL,
  EMPTY_PROPS_OBJECT_VERSIONS,
  EMPTY_PROPS_OBJECTS,
  EMPTY_PROPS_PROMPTS,
} from '../common/EmptyContent';
import {
  CustomLink,
  ObjectVersionLink,
  ObjectVersionsLink,
  objectVersionText,
  OpVersionLink,
  OpVersionsLink,
} from '../common/Links';
import {opNiceName} from '../common/opNiceName';
import {FilterLayoutTemplate} from '../common/SimpleFilterableDataTable';
import {
  buildDynamicColumns,
  prepareFlattenedDataForTable,
} from '../common/tabularListViews/columnBuilder';
import {TypeVersionCategoryChip} from '../common/TypeVersionCategoryChip';
import {useURLSearchParamsDict} from '../util';
import {
  KNOWN_BASE_OBJECT_CLASSES,
  OBJECT_ATTR_EDGE_NAME,
} from '../wfReactInterface/constants';
import {useWFHooks} from '../wfReactInterface/context';
import {
  isTableRef,
  makeRefExpandedPayload,
} from '../wfReactInterface/tsDataModelHooksCallRefExpansion';
import {
  objectVersionKeyToRefUri,
  opVersionKeyToRefUri,
} from '../wfReactInterface/utilities';
import {
  ObjectVersionSchema,
  OpVersionSchema,
} from '../wfReactInterface/wfDataModelHooksInterface';
import {WFHighLevelObjectVersionFilter} from './objectsPageTypes';

const DATASET_BASE_OBJECT_CLASS = 'Dataset';

type ObjectVersionRow = ObjectVersionSchema & {
  type: 'object';
  val: any;
};
type OpVersionRow = OpVersionSchema & {
  type: 'op';
  val: any;
};

type VersionRow = ObjectVersionRow | OpVersionRow;

type ObjectGridRow = {
  id: string;
  obj: VersionRow;
};
// Cache for storing call stats and peer versions query results
type QueryCache = {
  callStats: Map<string, number>;
  peerVersions: Map<string, {count: number; isLimited: boolean}>;
};

export const ObjectVersionsTable: React.FC<{
  objectVersions: VersionRow[];
  objectTitle?: string;
  hidePropsAsColumns?: boolean;
  hidePeerVersionsColumn?: boolean;
  hideCategoryColumn?: boolean;
  hideCreatedAtColumn?: boolean;
  hideVersionSuffix?: boolean;
  onRowClick?: (objectVersion: VersionRow) => void;
  selectedVersions?: string[];
  setSelectedVersions?: (selected: string[]) => void;
  customColumns?: GridColDef[];
  actionMenu?: (obj: VersionRow) => React.JSX.Element;
  keepNestedVal?: string[];
  queryCache: QueryCache;
}> = props => {
  // `showPropsAsColumns` probably needs to be a bit more robust
  const {selectedVersions, setSelectedVersions} = props;
  const showPropsAsColumns = !props.hidePropsAsColumns;
  const rows: GridRowsProp = useMemo(() => {
    const vals = props.objectVersions.map(ov => ov.val);
    const {flattenedData: flat} = prepareFlattenedDataForTable(vals);

    return props.objectVersions.map((ov, i) => {
      let val = flat[i];
      if (
        ov.type === 'object' &&
        ov.baseObjectClass === DATASET_BASE_OBJECT_CLASS
      ) {
        // We don't want to show the rows column for datasets
        // because it is redundant. Probably want a more generic
        // solution here in the future. Maybe exclude table refs?
        val = _.omit(val, 'rows');
      }
      // This is a dirty hack to enable keeping the nested query value for Monitors
      // allows to specify top-level fields from the underlying vals which are not
      // flattened into nested columns
      (props.keepNestedVal || []).forEach(keepVal => {
        if (keepVal in vals[i]) {
          val[keepVal] = vals[i][keepVal];
        }
      });
      // Show name, even though it can be = to object id, consider adding back
      // val = _.omit(val, 'name');
      return {
        id:
          ov.type === 'object'
            ? objectVersionKeyToRefUri(ov)
            : opVersionKeyToRefUri(ov),
        obj: {
          ...ov,
          val,
        },
      };
    });
  }, [props.objectVersions, props.keepNestedVal]);

  const showUserColumn = rows.some(row => row.obj.userId != null);

  // TODO: We should make this page very robust similar to the CallsTable page.
  // We will want to do nearly all the same things: URL state management,
  // sorting, filtering, ref expansion, etc... A lot of common logic should be
  // extracted and shared.
  const {cols: columns, groups: columnGroupingModel} = useMemo(() => {
    let groups: GridColumnGroupingModel = [];
    const checkboxColumnArr: GridColDef[] =
      selectedVersions != null && setSelectedVersions
        ? [
            {
              minWidth: 30,
              width: 34,
              field: 'CustomCheckbox',
              sortable: false,
              disableColumnMenu: true,
              resizable: false,
              disableExport: true,
              display: 'flex',
              renderHeader: (params: any) => {
                // TODO: Adding a select all checkbox here not that useful for compare
                // but might for be for other bulk actions.
                return null;
              },
              renderCell: (params: any) => {
                const rowObj = params.row.obj;
                const isOp = rowObj.type === 'op';

                if (isOp) {
                  // For ops, show a disabled checkbox with tooltip
                  return (
                    <Tooltip
                      content="Ops cannot be deleted"
                      trigger={
                        <div>
                          <Checkbox
                            size="small"
                            checked={false}
                            disabled={true}
                            onCheckedChange={() => {}}
                          />
                        </div>
                      }
                    />
                  );
                }

                // For regular objects, use the existing logic
                const {objectId, versionIndex} = rowObj;
                const objSpecifier = `${objectId}:v${versionIndex}`;
                const isSelected = selectedVersions.includes(objSpecifier);
                return (
                  <Checkbox
                    size="small"
                    checked={isSelected}
                    onCheckedChange={() => {
                      if (isSelected) {
                        setSelectedVersions(
                          selectedVersions.filter(id => id !== objSpecifier)
                        );
                      } else {
                        // Keep the objects in sorted order, regardless of the order checked.
                        setSelectedVersions(
                          [...selectedVersions, objSpecifier].sort((a, b) => {
                            const [aName, aVer] = a.split(':');
                            const [bName, bVer] = b.split(':');
                            if (aName !== bName) {
                              return aName.localeCompare(bName);
                            }
                            const aNum = parseInt(aVer.slice(1), 10);
                            const bNum = parseInt(bVer.slice(1), 10);
                            return aNum - bNum;
                          })
                        );
                      }
                    }}
                  />
                );
              },
            },
          ]
        : [];
    const cols: GridColDef[] = [
      ...checkboxColumnArr,

      // This field name chosen to reduce possibility of conflict
      // with the dynamic fields added below.
      basicField('weave__object_version_link', props.objectTitle ?? 'Object', {
        hideable: false,
        valueGetter: (unused: any, row: any) => {
          return row.obj.type === 'object'
            ? row.obj.objectId
            : opNiceName(row.obj.opId);
        },
        renderCell: cellParams => {
          // Icon to indicate navigation to the object version
          const obj: VersionRow = cellParams.row.obj;
          if (props.onRowClick) {
            let text = '';
            if (obj.type === 'object') {
              text = props.hideVersionSuffix
                ? obj.objectId
                : objectVersionText(obj.objectId, obj.versionIndex);
            } else {
              text = opNiceName(obj.opId);
            }

            // This allows us to use the object name as the link text
            // if it is available. Probably should make this workfor
            // the object version link as well.
            if (obj.val.name) {
              text = obj.val.name;
            }

            return (
              <CustomLink text={text} onClick={() => props.onRowClick?.(obj)} />
            );
          }
          if (obj.type === 'object') {
            return (
              <ObjectVersionLink
                entityName={obj.entity}
                projectName={obj.project}
                objectName={obj.objectId}
                version={obj.versionHash}
                versionIndex={obj.versionIndex}
                fullWidth={true}
                color={TEAL_600}
                hideVersionSuffix={props.hideVersionSuffix}
              />
            );
          } else {
            return (
              <OpVersionLink
                entityName={obj.entity}
                projectName={obj.project}
                opName={obj.opId}
                version={obj.versionHash}
                versionIndex={obj.versionIndex}
                fullWidth={true}
                color={TEAL_600}
              />
            );
          }
        },
      }),
    ];

    if (showPropsAsColumns) {
      const dynamicFields: string[] = [];
      const dynamicFieldSet = new Set<string>();
      rows.forEach(r => {
        Object.keys(r.obj.val).forEach(k => {
          if (!dynamicFieldSet.has(k)) {
            dynamicFieldSet.add(k);
            dynamicFields.push(k);
          }
        });
      });

      const {cols: newCols, groupingModel} = buildDynamicColumns<ObjectGridRow>(
        dynamicFields,
        row => ({
          entity: row.obj.entity,
          project: row.obj.project,
        }),
        (row, key) => {
          const obj = row.obj;
          const res = obj.val?.[key];
          if (isTableRef(res)) {
            // This whole block is a hack to make the table ref clickable. This
            // is the same thing that the CallsTable does for expanded fields.
            // Once we come up with a common pattern for ref expansion, this
            // will go away.
            const selfRefUri =
              obj.type === 'object'
                ? objectVersionKeyToRefUri(obj)
                : opVersionKeyToRefUri(obj);
            const targetRefUri =
              selfRefUri +
              ('/' +
                OBJECT_ATTR_EDGE_NAME +
                '/' +
                key.split('.').join(OBJECT_ATTR_EDGE_NAME + '/'));
            return makeRefExpandedPayload(targetRefUri, res);
          }
          return res;
        }
      );
      cols.push(...newCols);
      groups = groupingModel;
    }

    if (props.customColumns) {
      cols.push(...props.customColumns);
    }

    if (!props.hideCategoryColumn) {
      cols.push(
        basicField('baseObjectClass', 'Category', {
          width: 132,
          display: 'flex',
          valueGetter: (unused: any, row: any) => {
            if (row.obj.type === 'object') {
              return row.obj.baseObjectClass;
            }
            return 'Op';
          },
          renderCell: cellParams => {
            const category = cellParams.value;
            if (KNOWN_BASE_OBJECT_CLASSES.includes(category)) {
              return <TypeVersionCategoryChip baseObjectClass={category} />;
            } else if (category === 'Op') {
              // Adding 'Op' to KnownBaseObjectClassType has bad side effects
              return <Pill color={'teal'} label={'Ops'} />;
            }
            return null;
          },
        })
      );
    }

    if (showUserColumn) {
      cols.push(
        basicField('userId', 'User', {
          width: 150,
          filterable: false,
          sortable: false,
          valueGetter: (unused: any, row: any) => {
            return row.obj.userId;
          },
          renderCell: (params: any) => {
            const userId = params.value;
            if (userId == null) {
              return <div></div>;
            }
            return <UserLink userId={userId} includeName />;
          },
        })
      );
    }

    if (!props.hideCreatedAtColumn) {
      cols.push(
        basicField('createdAtMs', 'Last updated', {
          width: 130,
          valueGetter: (unused: any, row: any) => {
            return row.obj.createdAtMs;
          },
          renderCell: cellParams => {
            const createdAtMs = cellParams.value;
            return <Timestamp value={createdAtMs / 1000} format="relative" />;
          },
        })
      );
    }

    if (!props.hidePeerVersionsColumn) {
      cols.push(
        basicField('peerVersions', 'Versions', {
          width: 100,
          sortable: false,
          filterable: false,
          renderCell: cellParams => {
            const obj: VersionRow = cellParams.row.obj;
            if (obj.type === 'object') {
              return <ObjectPeerVersionsLink obj={obj} />;
            }
            return (
              <OpPeerVersionsLink obj={obj} queryCache={props.queryCache} />
            );
          },
        })
      );
    }

    if (props.actionMenu) {
      cols.push({
        field: 'actionMenu',
        headerName: '',
        renderCell: param =>
          props.actionMenu && props.actionMenu(param.row.obj),
      });
    }

    return {cols, groups};
  }, [
    props,
    showPropsAsColumns,
    rows,
    selectedVersions,
    setSelectedVersions,
    showUserColumn,
  ]);

  // Highlight table row if it matches peek drawer.
  const query = useURLSearchParamsDict();
  const {peekPath} = query;
  const peekId = peekPath ? peekPath.split('/').pop() : null;
  const rowIds = useMemo(() => {
    return rows.map(row => row.id);
  }, [rows]);
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  useEffect(() => {
    if (rowIds.length === 0) {
      // Data may have not loaded
      return;
    }
    if (peekId == null) {
      // No peek drawer, clear any selection
      setRowSelectionModel([]);
    } else {
      // If peek drawer matches a row, select it.
      // If not, don't modify selection.
      if (rowIds.includes(peekId)) {
        setRowSelectionModel([peekId]);
      }
    }
  }, [rowIds, peekId]);

  return (
    <StyledDataGrid
      // Start Column Menu
      // ColumnMenu is only needed when we have other actions
      // such as filtering.
      disableColumnMenu={true}
      // We don't have enough columns to justify filtering
      disableColumnFilter={true}
      disableMultipleColumnsFiltering={true}
      // ColumnPinning seems to be required in DataGridPro, else it crashes.
      disableColumnPinning={false}
      // We don't have enough columns to justify re-ordering
      disableColumnReorder={true}
      // The columns are fairly simple, so we don't need to resize them.
      disableColumnResize={false}
      // We don't have enough columns to justify hiding some of them.
      disableColumnSelector={true}
      // We don't have enough columns to justify sorting by multiple columns.
      disableMultipleColumnsSorting={true}
      // End Column Menu
      rows={rows}
      initialState={{
        sorting: {
          sortModel: [{field: 'createdAtMs', sort: 'desc'}],
        },
      }}
      columnHeaderHeight={40}
      rowHeight={38}
      columns={columns}
      disableRowSelectionOnClick
      rowSelectionModel={rowSelectionModel}
      columnGroupingModel={columnGroupingModel}
    />
  );
};

export const FilterableObjectVersionsTable: React.FC<{
  entity: string;
  project: string;
  frozenFilter?: WFHighLevelObjectVersionFilter;
  initialFilter?: WFHighLevelObjectVersionFilter;
  objectTitle?: string;
  hideCategoryColumn?: boolean;
  hideCreatedAtColumn?: boolean;
  customColumns?: GridColDef[];
  metadataOnly?: boolean;
  // Setting this will make the component a controlled component. The parent
  // is responsible for updating the filter.
  onFilterUpdate?: (filter: WFHighLevelObjectVersionFilter) => void;
  selectedVersions?: string[];
  setSelectedVersions?: (selected: string[]) => void;
  propsEmpty?: EmptyProps;
  actionMenu?: (obj: VersionRow) => React.JSX.Element;
  keepNestedVal?: string[];
  hidePeerVersionsColumn?: boolean;
  hideVersionSuffix?: boolean;
}> = props => {
  const {setSelectedVersions} = props;
  const {useRootObjectVersions, useOpVersions} = useWFHooks();

  const effectiveFilter = useMemo(() => {
    return {...props.initialFilter, ...props.frozenFilter};
  }, [props.initialFilter, props.frozenFilter]);

  const isOneObject = effectiveFilter.objectName != null;
  const effectivelyLatestOnly = !effectiveFilter.objectName;

  const filteredObjectVersions = useRootObjectVersions({
    entity: props.entity,
    project: props.project,
    filter: {
      baseObjectClasses: effectiveFilter.baseObjectClass
        ? [effectiveFilter.baseObjectClass]
        : undefined,
      excludeBaseObjectClasses:
        effectiveFilter.excludeBaseObjectClasses ?? undefined,
      objectIds: effectiveFilter.objectName
        ? [effectiveFilter.objectName]
        : undefined,
      latestOnly: effectivelyLatestOnly,
    },
    metadataOnly: props.metadataOnly ?? effectivelyLatestOnly,
  });

  // Only fetch op versions when showing "All Objects" (no baseObjectClass or objectName filter)
  const shouldIncludeOps = effectiveFilter.baseObjectClass === 'Op';

  const filteredOpVersions = useOpVersions({
    entity: props.entity,
    project: props.project,
    filter: {
      latestOnly: true,
    },
    metadataOnly: true,
    skip: !shouldIncludeOps,
  });

  // When the table reloads, clear any selected versions.
  // This is because we may be reloading because of a deletion, and
  // we don't want the deleted version to remain in the selected state if it is there.
  useEffect(() => {
    if (
      (filteredObjectVersions.loading ||
        (shouldIncludeOps && filteredOpVersions.loading)) &&
      setSelectedVersions
    ) {
      setSelectedVersions([]);
    }
  }, [
    filteredObjectVersions.loading,
    filteredOpVersions.loading,
    shouldIncludeOps,
    setSelectedVersions,
  ]);

  // cache results in the table, so scrolling doesn't cause re-query
  const queryCache = useMemo(
    (): QueryCache => ({
      callStats: new Map<string, number>(),
      peerVersions: new Map<string, {count: number; isLimited: boolean}>(),
    }),
    []
  );

  if (
    filteredObjectVersions.loading ||
    (shouldIncludeOps && filteredOpVersions.loading)
  ) {
    return <Loading centered />;
  }
  if (filteredObjectVersions.error) {
    return <ErrorPanel error={filteredObjectVersions.error} />;
  }
  if (shouldIncludeOps && filteredOpVersions.error) {
    return <ErrorPanel error={filteredOpVersions.error} />;
  }

  // TODO: Only show the empty state if no filters other than baseObjectClass
  const objectVersions: VersionRow[] = (
    filteredObjectVersions.result ?? []
  ).map(ov => ({...ov, type: 'object', val: ov.val ?? {}}));

  // Only include op versions when shouldIncludeOps is true
  const opVersions: VersionRow[] = shouldIncludeOps
    ? (filteredOpVersions.result ?? []).map(ov => ({
        ...ov,
        type: 'op',
        val: {},
      }))
    : [];
  const versions = [...objectVersions, ...opVersions];

  const isEmpty = versions.length === 0;
  if (isEmpty) {
    let propsEmpty =
      props.propsEmpty ||
      (isOneObject ? EMPTY_PROPS_OBJECT_VERSIONS : EMPTY_PROPS_OBJECTS);
    const base = props.initialFilter?.baseObjectClass;
    if ('Prompt' === base) {
      propsEmpty = EMPTY_PROPS_PROMPTS;
    } else if ('Model' === base) {
      propsEmpty = EMPTY_PROPS_MODEL;
    } else if (base === 'Leaderboard') {
      propsEmpty = EMPTY_PROPS_LEADERBOARDS;
    } else if (base === 'Evaluation') {
      propsEmpty = EMPTY_PROPS_EVALUATIONS;
    }
    return <Empty {...propsEmpty} />;
  }

  return (
    <FilterLayoutTemplate>
      <ObjectVersionsTable
        objectVersions={versions}
        objectTitle={props.objectTitle}
        hidePropsAsColumns={!!effectivelyLatestOnly}
        hidePeerVersionsColumn={
          props.hidePeerVersionsColumn ?? !effectivelyLatestOnly
        }
        hideCategoryColumn={props.hideCategoryColumn}
        hideCreatedAtColumn={props.hideCreatedAtColumn}
        selectedVersions={props.selectedVersions}
        setSelectedVersions={props.setSelectedVersions}
        customColumns={props.customColumns}
        actionMenu={props.actionMenu}
        keepNestedVal={props.keepNestedVal}
        hideVersionSuffix={props.hideVersionSuffix}
        queryCache={queryCache}
      />
    </FilterLayoutTemplate>
  );
};

const ObjectPeerVersionsLink: React.FC<{obj: ObjectVersionSchema}> = props => {
  const {useRootObjectVersions} = useWFHooks();

  const obj = props.obj;
  // Here, we really just want to know the count - and it should be calculated
  // by the server, not by the client. This is a performance optimization. In
  // the meantime we will just fetch the first 100 versions and display 99+ if
  // there are at least 100. Someone can come back and add `count` to the 3
  // query APIs which will make this faster.
  const objectVersionsNode = useRootObjectVersions({
    entity: obj.entity,
    project: obj.project,
    filter: {
      objectIds: [obj.objectId],
    },
    limit: 100,
    metadataOnly: true,
  });
  if (objectVersionsNode.loading) {
    return <LoadingDots />;
  }
  const countValue = objectVersionsNode.result?.length ?? 0;
  return (
    <ObjectVersionsLink
      entity={obj.entity}
      project={obj.project}
      filter={{
        objectName: obj.objectId,
      }}
      versionCount={Math.min(countValue, 99)}
      countIsLimited={countValue === 100}
      neverPeek
      variant="secondary"
    />
  );
};

const OpPeerVersionsLink: React.FC<{
  obj: OpVersionSchema;
  queryCache: QueryCache;
}> = ({obj, queryCache}) => {
  const {useOpVersions} = useWFHooks();

  // Create a cache key for this specific op
  const cacheKey = `${obj.entity}/${obj.project}/${obj.opId}`;

  // Check if we already have the result cached
  const cachedResult = queryCache.peerVersions.get(cacheKey);
  const shouldFetch = cachedResult === undefined;

  // Here, we really just want to know the count - and it should be calculated
  // by the server, not by the client. This is a performance optimization. In
  // the meantime we will just fetch the first 100 versions and display 99+ if
  // there are at least 100. Someone can come back and add `count` to the 3
  // query APIs which will make this faster.
  const ops = useOpVersions({
    entity: obj.entity,
    project: obj.project,
    filter: {
      opIds: [obj.opId],
    },
    limit: 100,
    metadataOnly: true,
    skip: !shouldFetch,
  });

  // Cache the result when available
  if (ops.result && shouldFetch) {
    const versionCount = ops.result.length;
    queryCache.peerVersions.set(cacheKey, {
      count: Math.min(versionCount, 99),
      isLimited: versionCount === 100,
    });
  }

  // Use cached value if available, otherwise use the current query result
  const result =
    cachedResult ??
    (ops.result
      ? {
          count: Math.min(ops.result.length, 99),
          isLimited: ops.result.length === 100,
        }
      : null);

  if (ops.loading && cachedResult === undefined) {
    return <LoadingDots />;
  }

  if (!result) {
    return <LoadingDots />;
  }

  return (
    <OpVersionsLink
      entity={obj.entity}
      project={obj.project}
      filter={{
        opName: obj.opId,
      }}
      versionCount={result.count}
      countIsLimited={result.isLimited}
      neverPeek
      variant="secondary"
    />
  );
};
